import { H2, P } from '@undp/design-system-react/Typography';
import sum from 'lodash.sum';
import { AnimatePresence, motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { Colors } from '@/Components/ColorPalette';
import { GraphArea, GraphContainer } from '@/Components/Elements/GraphContainer';
import { GraphFooter } from '@/Components/Elements/GraphFooter';
import { GraphHeader } from '@/Components/Elements/GraphHeader';
import type {
  AnimateDataType,
  ClassNameObject,
  Languages,
  NumberFormatOptions,
  SourcesDataType,
  StyleObject,
  UnitChartDataType,
} from '@/Types';
import { numberFormattingFunction } from '@/Utils/numberFormattingFunction';

interface Props {
  // Data
  /** Array of data objects */
  data: UnitChartDataType[];

  // Titles, Labels, and Sources
  /** Title of the graph */
  graphTitle?: string | React.ReactNode;
  /** Description of the graph */
  graphDescription?: string | React.ReactNode;
  /** Note with h2 tag just above the graph. Can be used to highlight text */
  note?: string;
  /** Footnote for the graph */
  footNote?: string | React.ReactNode;
  /** Source data for the graph */
  sources?: SourcesDataType[];
  /** Accessibility label */
  ariaLabel?: string;

  // Colors and Styling
  /** Colors of the highlighted circles */
  colors?: string[];
  /** Background color of the graph */
  backgroundColor?: string | boolean;
  /** Custom styles for the graph. Each object should be a valid React CSS style object. */
  styles?: StyleObject;
  /** Custom class names */
  classNames?: ClassNameObject;

  // Size and Spacing
  /** Width of the graph */
  width?: number;
  /** Height of the graph */
  height?: number;
  /** Minimum height of the graph */
  minHeight?: number;
  /** Relative height scaling factor. This overwrites the height props */
  relativeHeight?: number;
  /** Padding around the graph. Defaults to 0 if no backgroundColor is mentioned else defaults to 1rem */
  padding?: string;

  // Graph Parameters
  /** Size of the visualization */
  size?: number;
  /** No. of grid icons in a single row */
  gridSize?: number;
  /** Defines the `d` attribute values for the SVG paths used as grid icons. Each path should be designed for a 24px × 24px viewBox. The order of paths determines the rendering order. If no paths are provided, a circle icon is used as the default. */
  gridIcon?: { width?: number; height?: number; d: string[] };
  /** Spacing between 2 grid icons */
  unitPadding?: number;
  /** Total no. of grid icon that are rendered in the chart */
  totalNoOfDots?: number;
  /** Toggle visibility of stroke for the unfilled grid icons. */
  showStrokeForWhiteDots?: boolean;
  /** Toggles if the graph animates in when loaded.  */
  animate?: boolean | AnimateDataType;
  /** Toggle visibility of color scale */
  showColorScale?: boolean;
  /** Configuration options for controlling number formatting, localization, prefixes/suffixes, precision, and zero padding. */
  numberDisplayOptions?: NumberFormatOptions;
  /** Defines how “NA” values should be displayed/labelled in the graph */
  naLabel?: string;
  /** Enable graph download option as png */
  graphDownload?: boolean;
  /** Enable data download option as a csv */
  dataDownload?: boolean;

  // Configuration and Options
  /** Language setting  */
  language?: Languages;
  /** Color theme */
  theme?: 'light' | 'dark';
  /** Unique ID for the graph */
  graphID?: string;
}

export function UnitChart(props: Props) {
  const {
    data,
    size,
    graphTitle,
    sources,
    colors = Colors.light.categoricalColors.colors,
    graphDescription,
    totalNoOfDots = 100,
    unitPadding = 3,
    gridSize = 10,
    footNote,
    padding,
    backgroundColor = false,
    graphID,
    graphDownload = false,
    language = 'en',
    showColorScale = true,
    showStrokeForWhiteDots = true,
    note,
    dataDownload = false,
    theme = 'light',
    width,
    height,
    minHeight = 0,
    relativeHeight,
    ariaLabel,
    styles,
    classNames,
    animate = false,
    naLabel = 'NA',
    numberDisplayOptions,
    gridIcon,
  } = props;
  const svgRef = useRef(null);
  const animateValue =
    animate === true
      ? { duration: 0.5, once: true, amount: 0.5 }
      : animate || { duration: 0, once: true, amount: 0 };
  const isInView = useInView(svgRef, {
    once: animateValue.once,
    amount: animateValue.amount,
  });
  const sizeVal = size || gridIcon  ? (gridIcon?.width || 24) * gridSize + unitPadding * (gridSize - 1) : 200;
  const totalValue = sum(data.map((d) => d.value));
  const graphParentDiv = useRef<HTMLDivElement>(null);
  const gridDimension = sizeVal / gridSize;
  const radius = (gridDimension - unitPadding * 2) / 2;
  if (radius <= 0) {
    console.error(
      'The size of single unit is less than or equal to zero. Check values for ((dimension / gridSize) - (padding * 2)) / 2 is not less than or equal to 0.',
      `size: ${size}`,
      `gridSize: ${gridSize}`,
      `gridDimension: ${gridDimension}`,
      `unitPadding: ${unitPadding}`,
    );
    return null;
  }

  const cellsData: { color: string; index: number }[] = [];
  data.forEach((item, index) => {
    const count = Math.round((item.value / totalValue) * totalNoOfDots);
    for (let i = 0; i < count; i += 1) {
      cellsData.push({ color: colors[index], index });
    }
  });
  return (
    <GraphContainer
      className={classNames?.graphContainer}
      style={styles?.graphContainer}
      id={graphID}
      ref={graphParentDiv}
      aria-label={ariaLabel}
      backgroundColor={backgroundColor}
      theme={theme}
      language={language}
      minHeight={minHeight}
      width={width}
      height={height}
      relativeHeight={relativeHeight}
      padding={padding}
    >
      {graphTitle || graphDescription || graphDownload ? (
        <GraphHeader
          styles={{
            title: styles?.title,
            description: styles?.description,
          }}
          classNames={{
            title: classNames?.title,
            description: classNames?.description,
          }}
          graphTitle={graphTitle}
          graphDescription={graphDescription}
          width={width}
          graphDownload={graphDownload ? graphParentDiv : undefined}
          dataDownload={
            dataDownload
              ? data.map((d) => d.data).filter((d) => d !== undefined).length > 0
                ? data.map((d) => d.data).filter((d) => d !== undefined)
                : data.filter((d) => d !== undefined)
              : null
          }
        />
      ) : null}
      {note ? (
        <H2
          marginBottom='2xs'
          className='text-content-primary font-bold'
          style={{ width: width ? `${width}px` : '100%' }}
        >
          {note}
        </H2>
      ) : null}
      {showColorScale ? (
        <div className='mb-4 leading-0' style={{ width: width ? `${width}px` : '100%' }}>
          <div className='flex mb-0 flex-wrap gap-x-4 gap-y-1'>
            {data.map((d, i) => (
              <div className='flex gap-2 items-center' key={d.label}>
                <div className='w-3 h-3 rounded-full' style={{ backgroundColor: colors[i] }} />
                <P marginBottom='none' size='sm' className='text-content-primary'>
                  {d.label}:{' '}
                  <span className='font-bold'>
                    {numberFormattingFunction(
                      d.value,
                      naLabel,
                      numberDisplayOptions?.precision ?? 2,
                      numberDisplayOptions?.prefix,
                      numberDisplayOptions?.suffix,
                      numberDisplayOptions?.locale || 'en',
                      numberDisplayOptions?.padZeros || 'none',
                    )}
                  </span>
                </P>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      <GraphArea>
        <svg
          width={`${width || sizeVal}px`}
          height={`${Math.max(
            minHeight,
            height
              ? relativeHeight && width
                ? minHeight
                  ? width * relativeHeight > minHeight
                    ? width * relativeHeight
                    : minHeight
                  : width * relativeHeight
                : height
              : Math.floor((totalNoOfDots - 1) / gridSize) * gridDimension +
                  gridDimension / 2 +
                  radius +
                  5,
          )}px`}
          ref={svgRef}
          direction='ltr'
          viewBox={`0 0 ${width || sizeVal} ${Math.max(
            minHeight,
            height
              ? relativeHeight && width
                ? minHeight
                  ? width * relativeHeight > minHeight
                    ? width * relativeHeight
                    : minHeight
                  : width * relativeHeight
                : height
              : Math.floor((totalNoOfDots - 1) / gridSize) * gridDimension +
                  gridDimension / 2 +
                  radius +
                  5,
          )}`}
        >
          <AnimatePresence>
            <g>
              {gridIcon
                ? cellsData.map((d, i) => (
                    <motion.svg
                      key={d.index}
                      style={{
                        strokeWidth: 1,
                      }}
                      viewBox={`0 0 ${gridIcon?.width || 24} ${gridIcon?.height || 24}`}
                      width={gridIcon?.width || radius * 2}
                      height={gridIcon?.height || radius * 2}
                      variants={{
                        initial: {
                          fill: '#fff',
                          opacity: 0,
                          ...(!showStrokeForWhiteDots ? { stroke: d.color } : {}),
                          strokeWidth: 1,
                        },
                        whileInView: {
                          fill: d.color,
                          opacity: 1,
                          ...(!showStrokeForWhiteDots ? { stroke: d.color } : {}),
                          strokeWidth: 1,
                          x:
                            (i % gridSize) *
                            (gridIcon?.width ? gridIcon.width + unitPadding : gridDimension),
                          y:
                            Math.floor(i / gridSize) *
                            (gridIcon?.height ? gridIcon.height + unitPadding : gridDimension),
                          transition: {
                            duration: 0,
                            delay: (animateValue.duration / cellsData.length) * i,
                          },
                        },
                      }}
                      initial='initial'
                      animate={isInView ? 'whileInView' : 'initial'}
                      className={
                        (d.color.toLowerCase() === '#fff' ||
                          d.color.toLowerCase() === '#ffffff' ||
                          d.color.toLowerCase() === 'white') &&
                        showStrokeForWhiteDots
                          ? 'stroke-stroke'
                          : ''
                      }
                    >
                      {gridIcon.d.map((el) => (
                        <path d={el} key={el} />
                      ))}
                    </motion.svg>
                  ))
                : cellsData.map((d, i) => (
                    <motion.circle
                      key={d.index}
                      style={{
                        strokeWidth: 1,
                      }}
                      variants={{
                        initial: {
                          fill: '#fff',
                          opacity: 0,
                          ...(!showStrokeForWhiteDots ? { stroke: d.color } : {}),
                          strokeWidth: 1,
                        },
                        whileInView: {
                          fill: d.color,
                          opacity: 1,
                          ...(!showStrokeForWhiteDots ? { stroke: d.color } : {}),
                          strokeWidth: 1,
                          cx: (i % gridSize) * gridDimension + gridDimension / 2,
                          cy: Math.floor(i / gridSize) * gridDimension + gridDimension / 2,
                          transition: {
                            duration: 0,
                            delay: (animateValue.duration / cellsData.length) * i,
                          },
                        },
                      }}
                      initial='initial'
                      animate={isInView ? 'whileInView' : 'initial'}
                      className={
                        (d.color.toLowerCase() === '#fff' ||
                          d.color.toLowerCase() === '#ffffff' ||
                          d.color.toLowerCase() === 'white') &&
                        showStrokeForWhiteDots
                          ? 'stroke-stroke'
                          : ''
                      }
                      r={radius}
                    />
                  ))}
            </g>
          </AnimatePresence>
        </svg>
      </GraphArea>
      {sources || footNote ? (
        <GraphFooter
          styles={{
            footnote: styles?.footnote,
            source: styles?.source,
          }}
          sources={sources}
          footNote={footNote}
          width={width}
        />
      ) : null}
    </GraphContainer>
  );
}
